import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { useToast } from '../../notifications';
import { getReminderDefinition, listReminderChannels, listReminderSubjectParameters, listReminderSubjectTypes, upsertReminderDefinition } from '../services/reminderEngineService';
import { listReminderMessageTemplates } from '../services/messageTemplateService';
import type { ReminderEngineChannelInfo, ReminderEngineDefinition, ReminderEngineSubjectParameter, ReminderEngineSubjectType, UpsertReminderDefinitionRequest } from '../types/ReminderEngine';
import { categoryToSubjectType } from '../utils/definitionEditorForm';
import type { MessageTemplate } from '../../conversations/services/conversationService';
import { DefinitionEditorForm } from './DefinitionEditorForm';
import sharedStyles from './reminderEngineShared.module.css';

export const ReminderDefinitionEditPage: React.FC = () => {
  const { definitionKey = '' } = useParams<{ definitionKey: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['reminderEngine', 'common', 'nav']);
  const { showToast } = useToast();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [definition, setDefinition] = React.useState<ReminderEngineDefinition | null>(null);
  const [channels, setChannels] = React.useState<ReminderEngineChannelInfo[]>([]);
  const [subjectTypes, setSubjectTypes] = React.useState<ReminderEngineSubjectType[]>([]);
  const [subjectParameters, setSubjectParameters] = React.useState<ReminderEngineSubjectParameter[]>([]);
  const [messageTemplates, setMessageTemplates] = React.useState<MessageTemplate[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const decodedKey = React.useMemo(() => decodeURIComponent(definitionKey), [definitionKey]);

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [definitionItem, channelItems, typeItems, templateItems] = await Promise.all([
          getReminderDefinition(decodedKey),
          listReminderChannels(),
          listReminderSubjectTypes(),
          listReminderMessageTemplates(),
        ]);
        const subjectType = categoryToSubjectType(definitionItem.category ?? '') || typeItems[0]?.subjectType || '';
        const parameterItems = subjectType
          ? await listReminderSubjectParameters(subjectType)
          : [];
        if (!cancelled) {
          setDefinition(definitionItem);
          setChannels(channelItems);
          setSubjectTypes(typeItems);
          setSubjectParameters(parameterItems);
          setMessageTemplates(templateItems);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('loadFailed'));
          setDefinition(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (decodedKey) {
      void load();
    } else {
      setLoading(false);
      setError(t('definitionNotFound'));
    }

    return () => {
      cancelled = true;
    };
  }, [decodedKey, t]);

  const handleSave = async (payload: UpsertReminderDefinitionRequest) => {
    setSaving(true);
    setSaveError(null);
    try {
      await upsertReminderDefinition(payload);
      showToast(t('definitionSaved'), 'success');
      navigate('/reminder-engine/definitions');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('actionFailed');
      setSaveError(message);
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={sharedStyles.page}>
      <Breadcrumb
        items={[
          { label: t('reminderEngine', { ns: 'nav' }), path: '/reminder-engine/definitions' },
          { label: t('definitionsTitle'), path: '/reminder-engine/definitions' },
          { label: definition?.name ?? t('editDefinition'), path: `/reminder-engine/definitions/${encodeURIComponent(decodedKey)}/edit` },
        ]}
      />

      <div className={sharedStyles.headerRow}>
        <div>
          <h2>{t('editDefinition')}</h2>
          <p className={sharedStyles.pageIntro}>{t('editDefinitionSubtitle')}</p>
        </div>
        <Link to="/reminder-engine/definitions" className="btn btn-secondary">
          {t('backToDefinitions')}
        </Link>
      </div>

      {loading && <Spinner />}
      {error && (
        <section className={sharedStyles.sectionCard}>
          <p className={sharedStyles.error}>{error}</p>
          <Link to="/reminder-engine/definitions" className="btn btn-secondary">
            {t('backToDefinitions')}
          </Link>
        </section>
      )}

      {!loading && !error && definition && (
        <section className={sharedStyles.sectionCard}>
          <DefinitionEditorForm
            definition={definition}
            channels={channels}
            messageTemplates={messageTemplates}
            subjectTypes={subjectTypes}
            subjectParameters={subjectParameters}
            busy={saving}
            error={saveError}
            onCancel={() => navigate('/reminder-engine/definitions')}
            onSave={handleSave}
          />
        </section>
      )}
    </div>
  );
};
